/**
 * Theme and language, shared with the Lab hub through same-origin localStorage
 * (`lab:theme`, `lab:lang`). The pelican scenes app is a separate Worker, so it
 * only reads/writes those keys and mirrors the hub's dark tokens; a `storage`
 * event keeps the two in sync when both are open.
 */
import { useCallback, useEffect, useState } from 'react'
import { parseLang, type Lang } from './lang'

export type Theme = 'light' | 'dark'
const THEME_KEY = 'lab:theme'
const LANG_KEY = 'lab:lang'

function readTheme(): Theme {
  if (typeof document !== 'undefined') {
    const d = document.documentElement.dataset.theme
    if (d === 'light' || d === 'dark') return d
  }
  try {
    const s = localStorage.getItem(THEME_KEY)
    if (s === 'light' || s === 'dark') return s
  } catch { /* storage blocked */ }
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function readLang(): Lang {
  if (typeof window !== 'undefined') {
    const fromUrl = parseLang(new URLSearchParams(window.location.search).get('lang'))
    if (fromUrl) return fromUrl
  }
  try {
    const stored = parseLang(localStorage.getItem(LANG_KEY))
    if (stored) return stored
  } catch { /* storage blocked */ }
  return 'en'
}

function writeLangToUrl(lang: Lang) {
  const url = new URL(window.location.href)
  // English is canonical and unprefixed. Keep hash and every non-language
  // query (result filters, share anchors) intact.
  if (lang === 'en') url.searchParams.delete('lang')
  else url.searchParams.set('lang', 'zh-CN')
  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`)
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(readTheme)
  useEffect(() => {
    document.documentElement.dataset.theme = theme
    try { localStorage.setItem(THEME_KEY, theme) } catch { /* storage blocked */ }
  }, [theme])
  useEffect(() => {
    const on = (e: StorageEvent) => { if (e.key === THEME_KEY && (e.newValue === 'light' || e.newValue === 'dark')) setTheme(e.newValue) }
    window.addEventListener('storage', on)
    return () => window.removeEventListener('storage', on)
  }, [])
  const toggle = useCallback(() => setTheme((t) => (t === 'dark' ? 'light' : 'dark')), [])
  return { theme, toggle }
}

export function useLang() {
  const [lang, setLangState] = useState<Lang>(readLang)
  useEffect(() => {
    const on = (e: StorageEvent) => { if (e.key === LANG_KEY) setLangState(readLang()) }
    window.addEventListener('storage', on)
    return () => window.removeEventListener('storage', on)
  }, [])
  const setLang = useCallback((l: Lang) => {
    try { localStorage.setItem(LANG_KEY, l) } catch { /* storage blocked */ }
    writeLangToUrl(l)
    setLangState(l)
  }, [])
  return { lang, setLang }
}

export function ThemeToggle({ lang }: { lang: Lang }) {
  const { theme, toggle } = useTheme()
  const dark = theme === 'dark'
  const label = lang === 'zh-CN' ? (dark ? '切换到浅色主题' : '切换到深色主题') : (dark ? 'Switch to light theme' : 'Switch to dark theme')
  return (
    <button type="button" className="icon-btn" aria-pressed={dark} aria-label={label} title={label} onClick={toggle}>
      {dark
        ? <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><path d="M15.5 11.5A6 6 0 0 1 8.5 4.5a6 6 0 1 0 7 7Z" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
        : <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true"><circle cx="10" cy="10" r="3.5" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M10 2.5v2M10 15.5v2M2.5 10h2M15.5 10h2M4.7 4.7l1.4 1.4M13.9 13.9l1.4 1.4M15.3 4.7l-1.4 1.4M6.1 13.9l-1.4 1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>}
    </button>
  )
}

export function LangToggle({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div className="lab-lang" role="group" aria-label={lang === 'zh-CN' ? '语言' : 'Language'}>
      <button type="button" aria-pressed={lang === 'en'} onClick={() => setLang('en')}>EN</button>
      <button type="button" aria-pressed={lang === 'zh-CN'} lang="zh-CN" onClick={() => setLang('zh-CN')}>中</button>
    </div>
  )
}
